'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { useFormattedCurrency } from '@/lib/currency';
import { getAnimatedD6Formatter } from '@/lib/format/animated-d6';
import { getFundingProgress } from '@/lib/format/funding-progress';
import { MOTION_POSITION } from '@/lib/ui/animation';
import { cn } from '@/lib/ui/utils';
export interface ProposalFundingStatusProps {
  totalFunded: string;
  creationThreshold: string | null;
  muted?: boolean;
  animateValuesOnMount?: boolean;
}

// Module-scope motion variants — React Compiler-friendly (no inline-object
// identity churn). Fill changes are "position" transitions per the Easing
// Blueprint (a value moving A → B), so we reuse `MOTION_POSITION` from the
// project's animation tokens rather than hand-rolling a curve+duration.
const FILL_TRANSITION_REDUCED = {
  duration: 0
} as const;
export function ProposalFundingStatus(t0) {
  const $ = _c(63);
  const {
    totalFunded,
    creationThreshold,
    muted: t1,
    animateValuesOnMount: t2
  } = t0;
  const muted = t1 === undefined ? false : t1;
  const animateValuesOnMount = t2 === undefined ? true : t2;
  const tCard = useTranslations("launchpad.card");
  const tFeed = useTranslations("launchpad.feed");
  const {
    formatD6
  } = useFormattedCurrency();
  const prefersReduced = useReducedMotion();
  let t3;
  if ($[0] !== creationThreshold || $[1] !== totalFunded) {
    t3 = getFundingProgress(totalFunded, creationThreshold);
    $[0] = creationThreshold;
    $[1] = totalFunded;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const progress = t3;
  const hasThreshold = progress.displayPercent !== null;
  const normalizedFunded = totalFunded.length > 0 ? totalFunded : "0";
  const normalizedThreshold = creationThreshold == null || creationThreshold.length === 0 ? "0" : creationThreshold;
  let t4;
  if ($[3] !== normalizedFunded) {
    t4 = BigInt(normalizedFunded);
    $[3] = normalizedFunded;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const raisedNumber = Number(t4);
  let t5;
  if ($[5] !== normalizedThreshold) {
    t5 = BigInt(normalizedThreshold);
    $[5] = normalizedThreshold;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const targetNumber = Number(t5);
  const isOverbaked = hasThreshold && BigInt(normalizedFunded) > BigInt(normalizedThreshold);
  const t6 = isOverbaked ? "compactTight" : "compactWhole";
  let t7;
  if ($[7] !== formatD6 || $[8] !== normalizedFunded || $[9] !== t6) {
    t7 = getAnimatedD6Formatter(formatD6, normalizedFunded, t6);
    $[7] = formatD6;
    $[8] = normalizedFunded;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const formatRaised = t7;
  let t10;
  let t11;
  let t12;
  let t13;
  let t8;
  let t9;
  if ($[11] !== animateValuesOnMount || $[12] !== formatD6 || $[13] !== formatRaised || $[14] !== hasThreshold || $[15] !== muted || $[16] !== normalizedThreshold || $[17] !== progress.displayPercent || $[18] !== raisedNumber || $[19] !== targetNumber) {
    const formatTarget = getAnimatedD6Formatter(formatD6, normalizedThreshold, "compactWhole");
    const t14 = muted && "opacity-60";
    if ($[26] !== t14) {
      t12 = cn("flex flex-col gap-1.5", t14);
      $[26] = t14;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    t13 = "proposal-funding-status";
    t8 = "flex flex-wrap items-center gap-1 text-body font-medium";
    if ($[28] !== animateValuesOnMount || $[29] !== formatRaised || $[30] !== raisedNumber) {
      t9 = <AnimatedNumber value={raisedNumber} format={formatRaised} animateOnMount={animateValuesOnMount} className="text-foreground-primary" />;
      $[28] = animateValuesOnMount;
      $[29] = formatRaised;
      $[30] = raisedNumber;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <span className="text-foreground-tertiary">/</span>;
      $[32] = t10;
    } else {
      t10 = $[32];
    }
    t11 = hasThreshold ? <><AnimatedNumber value={targetNumber} format={formatTarget} animateOnMount={animateValuesOnMount} className="text-foreground-primary" /><span className="ml-auto text-caption text-foreground-tertiary tabular-nums">{String(progress.displayPercent)}%</span></> : <span className="text-foreground-tertiary">{formatTarget(0)}</span>;
    $[11] = animateValuesOnMount;
    $[12] = formatD6;
    $[13] = formatRaised;
    $[14] = hasThreshold;
    $[15] = muted;
    $[16] = normalizedThreshold;
    $[17] = progress.displayPercent;
    $[18] = raisedNumber;
    $[19] = targetNumber;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t8;
    $[25] = t9;
  } else {
    t10 = $[20];
    t11 = $[21];
    t12 = $[22];
    t13 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  let t14;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t8 || $[36] !== t9) {
    t14 = <div className={t8}>{t9}{t10}{t11}</div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t8;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== tFeed) {
    t15 = tFeed("fundingProgressLabel");
    $[38] = tFeed;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== progress.displayPercent || $[41] !== tCard) {
    t16 = progress.displayPercent === null ? undefined : tCard("fundingProgress", {
      percent: String(progress.displayPercent)
    });
    $[40] = progress.displayPercent;
    $[41] = tCard;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const t17 = progress.displayPercent === null ? undefined : Math.min(progress.displayPercent, 100);
  let t18;
  if ($[43] !== t15 || $[44] !== t16 || $[45] !== t17) {
    t18 = <progress className="sr-only" aria-label={t15} aria-valuetext={t16} max={100} value={t17} />;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = cn("h-full w-full origin-left rounded-full", "bg-funding-progress");
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== animateValuesOnMount || $[49] !== prefersReduced) {
    t20 = prefersReduced === true || !animateValuesOnMount ? false : {
      scaleX: 0
    };
    $[48] = animateValuesOnMount;
    $[49] = prefersReduced;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const t21 = progress.fillPercent / 100;
  let t22;
  if ($[51] !== t21) {
    t22 = {
      scaleX: t21
    };
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  const t23 = prefersReduced === true ? FILL_TRANSITION_REDUCED : MOTION_POSITION;
  let t24;
  if ($[53] !== t20 || $[54] !== t22 || $[55] !== t23) {
    t24 = <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-tertiary" data-testid="proposal-funding-status-bar"><LazyMotion features={domAnimation}><m.div className={t19} initial={t20} animate={t22} transition={t23} data-testid="proposal-funding-status-bar-fill" /></LazyMotion></div>;
    $[53] = t20;
    $[54] = t22;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t12 || $[58] !== t13 || $[59] !== t14 || $[60] !== t18 || $[61] !== t24) {
    t25 = <div className={t12} data-testid={t13}>{t14}{t18}{t24}</div>;
    $[57] = t12;
    $[58] = t13;
    $[59] = t14;
    $[60] = t18;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  return t25;
}